import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api, inr } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag, Bookmark, Copy, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Cart() {
  const navigate = useNavigate();
  const { items, updateQty, removeItem, subtotal, replaceItems } = useCart();
  const [params, setParams] = useSearchParams();
  const [saveOpen, setSaveOpen] = useState(false);

  // Restore a saved cart from a shared link (?c=CODE)
  useEffect(() => {
    const code = params.get("c");
    if (!code) return;
    (async () => {
      try {
        const { data } = await api.get(`/saved-carts/${code}`);
        if (data.items?.length) {
          replaceItems(data.items);
          toast.success("Saved cart restored — welcome back!");
        }
      } catch {
        toast.error("That saved cart link is invalid or expired.");
      } finally {
        params.delete("c");
        setParams(params, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (items.length === 0) {
    return (
      <div className="px-4 md:px-8 py-20 text-center" data-testid="empty-cart">
        <div className="h-16 w-16 rounded-2xl bg-muted grid place-items-center mx-auto mb-4">
          <ShoppingBag className="h-7 w-7 text-muted-foreground" />
        </div>
        <h1 className="font-display font-bold text-2xl">Your cart is empty</h1>
        <p className="text-muted-foreground mt-1">Add services from vendors to get started.</p>
        <button
          data-testid="browse-vendors-btn"
          onClick={() => navigate("/vendors")}
          className="mt-6 px-6 h-11 bg-primary text-white rounded-full font-semibold"
        >
          Browse vendors
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 pb-28">
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-5">Your cart</h1>
      <div className="grid gap-3">
        {items.map((i) => (
          <div
            key={i.service_id}
            data-testid={`cart-item-${i.service_id}`}
            className="flex items-center gap-3 bg-card border border-stone-200 rounded-2xl p-3"
          >
            <img src={i.image} alt={i.name} className="h-16 w-16 rounded-xl object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground">{i.vendor_name}</p>
              <h3 className="font-semibold leading-tight">{i.name}</h3>
              <p className="text-sm font-bold mt-0.5">
                {inr(i.price)} <span className="text-xs font-normal text-muted-foreground">{i.unit}</span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <button data-testid={`remove-${i.service_id}`} onClick={() => removeItem(i.service_id)}>
                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors" />
              </button>
              <div className="flex items-center gap-2 border border-stone-200 rounded-full px-1">
                <button
                  data-testid={`dec-${i.service_id}`}
                  onClick={() => updateQty(i.service_id, i.quantity - 1)}
                  className="h-7 w-7 grid place-items-center"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="text-sm font-semibold w-4 text-center">{i.quantity}</span>
                <button
                  data-testid={`inc-${i.service_id}`}
                  onClick={() => updateQty(i.service_id, i.quantity + 1)}
                  className="h-7 w-7 grid place-items-center"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-card border border-stone-200 rounded-2xl p-5">
        <div className="flex items-center justify-between text-lg">
          <span className="font-semibold">Subtotal</span>
          <span data-testid="cart-subtotal" className="font-display font-extrabold">{inr(subtotal)}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Taxes & final quote confirmed with the vendor after booking.
        </p>
        <button
          data-testid="proceed-checkout-btn"
          onClick={() => navigate("/checkout")}
          className="mt-4 w-full h-12 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-colors"
        >
          Proceed to checkout
        </button>
        <button
          data-testid="save-cart-btn"
          onClick={() => setSaveOpen(true)}
          className="mt-3 w-full h-11 border border-primary text-primary rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-accent transition-colors"
        >
          <Bookmark className="h-4 w-4" /> Save cart for later
        </button>
      </div>

      <SaveCartDialog open={saveOpen} setOpen={setSaveOpen} items={items} />
    </div>
  );
}

function SaveCartDialog({ open, setOpen, items }) {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.map((i) => ({
          service_id: i.service_id, vendor_id: i.vendor_id, vendor_name: i.vendor_name,
          name: i.name, price: i.price, unit: i.unit, image: i.image || "", quantity: i.quantity,
        })),
        email: email || null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/saved-carts", payload);
      const url = `${window.location.origin}/cart?c=${data.code}`;
      setLink(url);
      toast.success(email ? "Saved! We've emailed you the link too." : "Cart saved! Copy your link below.");
    } catch {
      toast.error("Could not save your cart. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — select the link manually.");
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) { setLink(""); setEmail(""); setCopied(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Save your cart</DialogTitle>
        </DialogHeader>
        {!link ? (
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">
              Get a private link to reopen this cart anytime on any device. Add your email and we'll send it to you too.
            </p>
            <Input
              data-testid="save-cart-email"
              type="email"
              placeholder="Email (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              data-testid="save-cart-submit"
              onClick={save}
              disabled={saving}
              className="h-11 bg-primary text-white rounded-full font-semibold disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save & get link"}
            </button>
          </div>
        ) : (
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">Your cart is saved. Reopen it with this link:</p>
            <div className="flex items-center gap-2 bg-muted rounded-xl p-2">
              <span data-testid="saved-cart-link" className="text-sm truncate flex-1 pl-1">{link}</span>
              <button
                data-testid="copy-cart-link"
                onClick={copy}
                className="shrink-0 h-9 px-3 bg-primary text-white rounded-full text-sm font-semibold flex items-center gap-1.5"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
